import { Button } from "@/components/ui/button";
import useAccessTokenData from "@/hooks/use-access-token-data";
import { cn } from "@/lib/utils";
import { Users } from "lucide-react";
import { FC, useCallback, useRef, useState } from "react";
import {
  triggerHapticFeedback,
  HAPTIC_FEEDBACK,
} from "@/utils/haptic-feedback";

interface UserSwitchButtonProps {
  onActivate: () => void;
  isActive?: boolean;
}

const UserSwitchButton: FC<UserSwitchButtonProps> = ({
  onActivate,
  isActive = false,
}) => {
  const accessTokenData = useAccessTokenData();
  const [isPressed, setIsPressed] = useState(false);
  const [pressProgress, setPressProgress] = useState(0);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const vibrationInterval = useRef<NodeJS.Timeout | null>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const startProgress = useCallback(() => {
    setPressProgress(0);
    progressInterval.current = setInterval(() => {
      setPressProgress((prev) => {
        const newProgress = prev + 100 / 30;
        if (newProgress >= 100) {
          clearInterval(progressInterval.current!);
          return 100;
        }
        return newProgress;
      });
    }, 100);
  }, []);

  const stopProgress = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    setPressProgress(0);
  }, []);

  const startVibration = useCallback(() => {
    const impactTypes = [
      HAPTIC_FEEDBACK.LIGHT,
      HAPTIC_FEEDBACK.MEDIUM,
      HAPTIC_FEEDBACK.HEAVY,
      HAPTIC_FEEDBACK.RIGID,
      HAPTIC_FEEDBACK.SOFT,
    ];
    let intensity = 0;

    vibrationInterval.current = setInterval(() => {
      intensity = (intensity + 1) % impactTypes.length;
      triggerHapticFeedback(impactTypes[intensity]);
    }, 100);
  }, []);

  const stopVibration = useCallback(() => {
    if (vibrationInterval.current) {
      clearInterval(vibrationInterval.current);
      vibrationInterval.current = null;
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsPressed(true);
    startProgress();
    startVibration();
    longPressTimer.current = setTimeout(() => {
      onActivate();
      stopVibration();
      stopProgress();
    }, 3000);
  }, [onActivate, startVibration, stopVibration, startProgress, stopProgress]);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    stopVibration();
    stopProgress();
  }, [stopVibration, stopProgress]);

  const handleMouseLeave = useCallback(() => {
    setIsPressed(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    stopVibration();
    stopProgress();
  }, [stopVibration, stopProgress]);

  const handleTouchStart = useCallback(() => {
    setIsPressed(true);
    startProgress();
    startVibration();
    longPressTimer.current = setTimeout(() => {
      onActivate();
      stopVibration();
      stopProgress();
    }, 3000);
  }, [onActivate, startVibration, stopVibration, startProgress, stopProgress]);

  const handleTouchEnd = useCallback(() => {
    setIsPressed(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    stopVibration();
    stopProgress();
  }, [stopVibration, stopProgress]);

  if (
    !accessTokenData?.switchInfoId ||
    !accessTokenData?.switchInfoLength ||
    accessTokenData?.switchInfoIndex === undefined
  ) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "relative transition-all duration-200 overflow-hidden select-none",
        isActive && "bg-primary/10 text-primary",
        isPressed && "scale-95"
      )}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <Users className="h-4 w-4 relative z-10 select-none" />

      {isPressed && pressProgress > 0 && (
        <div
          className="absolute inset-0 bg-primary/20 transition-all duration-100 ease-linear select-none"
          style={{
            width: `${pressProgress}%`,
            left: 0,
            top: 0,
            bottom: 0,
          }}
        />
      )}
    </Button>
  );
};

export default UserSwitchButton;
